import { InputHTMLAttributes, useMemo } from 'react';
import * as Yup from 'yup';
import { Input } from './Input';
import { GenericInputProps } from './GenericInput';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useFormikError, useOptionalField } from './FormikFields';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

interface Props extends InputHTMLAttributes<HTMLInputElement> {}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: theme.marginBase * 3,
    marginBottom: theme.marginBase * 3,
  },
  constraints: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  constraint: {
    ...theme.fonts.caption,
    color: theme.colors.inkTertiary,
    flex: 1,
    minWidth: '50%',
  },
  constraintValid: {
    color: theme.colors.greenAccessible,
  },
  constraintError: {
    color: theme.colors.redAccessible,
  },
  error: {
    ...theme.fonts.caption,
    color: theme.colors.red,
  },
  icon: {
    fontSize: theme.iconSize.small,
    marginRight: theme.marginBase / 2,
  },
}));

export const passwordSchema = Yup.string()
  .min(8, 'length')
  .matches(/[A-Z]/, 'upper')
  .matches(/[a-z]/, 'lower')
  .matches(/[0-9]/, 'number')
  .matches(/[^a-zA-Z0-9\s]/, 'symbol')
  .required('empty');

const constraints: string[] = ['length', 'upper', 'lower', 'number', 'symbol'];

export const PasswordInput = (props: Props & Partial<GenericInputProps>) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const {
    field: { value: password },
  } = useOptionalField(props);

  const errorMsg = useFormikError(props);

  const errors: string[] = useMemo(() => {
    try {
      passwordSchema.validateSync(password, { abortEarly: false });
      return [];
    } catch (e: any) {
      return e.errors;
    }
  }, [password]);

  return (
    <div className={classes.container}>
      <Input placeholder="****" {...props} noError type="password" noMargin />
      <div className={classes.constraints}>
        {constraints.map((cons) => (
          <div
            className={classNames(classes.constraint, {
              [classes.constraintValid]: !errors.includes(cons),
              [classes.constraintError]: errors.includes(cons) && !!errorMsg,
            })}
          >
            <i
              className={classNames(classes.icon, {
                'ri-check-line': !errors.includes(cons),
                'ri-close-line': errors.includes(cons),
              })}
            />
            {t('form.passwordRules.' + cons)}
          </div>
        ))}
      </div>
    </div>
  );
};
